import {
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  lighten,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import React, { useContext, useState } from "react";
import { accessControl, screeningStatuses } from "../../constants/enums";

import Button from "../Button";
import { CefSharpShell } from "../../helpers/CefSharpShell";
import ConfirmDialog from "../ConfirmDialog";
import { GetCancerScreeningResultWithQueryModel } from "../../models/getCancerScreeningResultModel";
import { LangContext } from "../../context/LangContext";
import ScreeningRequestDetail from "./ScreeningRequestDetail";
import TestResults from "./TestResults";
import { UserContext } from "../../context/UserContext";
import { hasUserAccess } from "../../utils";
import translations from "../../assets/json/translations.json";
import { useHistory } from "react-router-dom";

enum AnalysisTypeEnum {
  Pathology = 0,
  HPV = 1,
  Both = 2,
}

interface IProps {
  screeningRequest: GetCancerScreeningResultWithQueryModel;
  completeCancerScreeningInParent: (
    id: number,
    userSsn: string
  ) => Promise<void>;
  createAnalysisRequestInParent: (
    id: number,
    userSsn: string,
    userName: string,
    originalUserSsn: string,
    analysisType: number
  ) => Promise<void>;
  resendAnalysisRequestInParent: (
    id: number,
    userSsn: string,
    userName: string,
    originalUserSsn: string,
    analysisType: number
  ) => Promise<void>;
  removeScreeningTestsAndNotifyInParent: (
    id: number,
    userSsn: string
  ) => Promise<void>;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(5),
  },
  topItems: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.only("md")]: {
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
    },
  },
  titleDivider: {
    display: "inline-block",
    margin: theme.spacing(0, 1),
  },
  titleSerialNumber: {
    display: "inline-block",
    marginRight: theme.spacing(1),
  },
  otherDetails: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
  },
  descriptionTitle: {
    fontWeight: "bold",
    marginRight: theme.spacing(1),
    display: "inline-block",
  },
  descriptionDetails: {
    color: theme.palette.text.secondary,
    display: "inline-block",
  },
  resultsWrapper: {
    backgroundColor: lighten(theme.palette.secondary.light, 0.8),
    padding: theme.spacing(2),
  },
}));

const SentScreeningRequestDetails: React.FC<IProps> = ({
  screeningRequest,
  completeCancerScreeningInParent,
  createAnalysisRequestInParent,
  resendAnalysisRequestInParent,
  removeScreeningTestsAndNotifyInParent,
}) => {
  const classes = useStyles();
  const [lang] = useContext(LangContext);
  const history = useHistory();
  const theme = useTheme();
  const shellHelper = new CefSharpShell();

  const [openCompleteRequestDialog, setOpenCompleteRequestDialog] =
    useState(false);
  const [openRemoveTestsDialog, setOpenRemoveDialog] = useState(false);
  const [openCreateTestRequestDialog, setOpenCreateTestRequestDialog] =
    useState(false);
  const [openResendTestRequestDialog, setOpenResendTestRequestDialog] =
    useState(false);
  const [openPrintDialog, setOpenPrintDialog] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);

  const [analysisType, setAnalysisType] = useState<number | null>(null);
  const [user] = useContext(UserContext);
  const xsDown = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <Paper elevation={2} className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ScreeningRequestDetail
            screeningRequest={screeningRequest.cancerScreening}
          />
        </Grid>
        <Grid item xs={12}>
          <TestResults screeningRequest={screeningRequest} />
        </Grid>

        <Grid item xs={12}>
          {renderButtons()}
        </Grid>
      </Grid>
      {renderDialogs()}
    </Paper>
  );

  function renderButtons() {
    return (
      <Grid
        container
        spacing={2}
        justify="flex-end"
        direction="row"
        alignItems="stretch"
        wrap={xsDown ? "wrap" : "nowrap"}
      >
        <Grid item>
          {hasUserAccess(
            accessControl.SENT_SCREENING_REQUESTS_FULL_ACCESS,
            user
          ) && (
            <Button
              onClick={() => setOpenCompleteRequestDialog(true)}
              variant="contained"
              disabled={
                screeningRequest.cancerScreening.screeningStatus !==
                screeningStatuses.ALL_RESULTS_ARRIVED
              }
            >
              {translations.completeResults[lang]}
            </Button>
          )}
        </Grid>
        <Grid item>
          <Button
            onClick={() => setOpenCreateTestRequestDialog(true)}
            color="secondary"
            disabled={
              screeningRequest.cancerScreening.hpvTestCreateDate !== null &&
              screeningRequest.cancerScreening.pathologyTestCreateDate !== null
            }
          >
            {translations.createTestRequest[lang]}
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={() => setOpenResendTestRequestDialog(true)}
            color="secondary"
            disabled={resendTestRequestDisabled(screeningRequest)}
          >
            {translations.resendTestRequest[lang]}
          </Button>
        </Grid>
        <Grid item>
          <Button onClick={() => setOpenPrintDialog(true)} color="secondary">
            {translations.reprintScreeningNumber[lang]}
          </Button>
        </Grid>
        {hasUserAccess(
            accessControl.SENT_SCREENING_REQUESTS_FULL_ACCESS,
            user
        ) && (
        <Grid item>
              <Button
                  onClick={() => setOpenRemoveDialog(true)}
                  variant="contained"
              >
                {translations.removeScreeningTests[lang]}
              </Button>
        </Grid>
        )}
        <Grid item>
          <Button
            onClick={() => history.push("/sent-screening-requests")}
            variant="outlined"
          >
            {translations.back[lang]}
          </Button>
        </Grid>
      </Grid>
    );
  }

  function renderDialogs() {
    return (
      <>
        <ConfirmDialog
          open={openCreateTestRequestDialog}
          title={translations.createTestRequest[lang]}
          onCancel={() => clearDialogs()}
          onConfirm={() =>
            screeningRequest.cancerScreening.id &&
            user.ssn &&
            (analysisType === AnalysisTypeEnum.Pathology ||
              analysisType === AnalysisTypeEnum.HPV) &&
            createAnalysisRequest(
              screeningRequest.cancerScreening.id,
              user.ssn,
              user.name,
              screeningRequest.cancerScreening.requestorSsn ?? user.ssn,
              analysisType
            )
          }
          confirmText={translations.create[lang]}
          text={translations.analysisType[lang]}
          disableConfirm={
            analysisType === null ||
            analysisType === AnalysisTypeEnum.Both ||
            (analysisType === AnalysisTypeEnum.HPV &&
              screeningRequest.cancerScreening.hpvTestCreateDate !== null) ||
            (analysisType === AnalysisTypeEnum.Pathology &&
              screeningRequest.cancerScreening.pathologyTestCreateDate !== null)
          }
          loading={infoLoading}
        >
          {getCreateTestRequestDialogContent()}
        </ConfirmDialog>

        <ConfirmDialog
          open={openResendTestRequestDialog}
          title={translations.resendTestRequest[lang]}
          onCancel={() => clearDialogs()}
          onConfirm={() =>
            screeningRequest.cancerScreening.id &&
            user.ssn &&
            (analysisType === AnalysisTypeEnum.Pathology ||
              analysisType === AnalysisTypeEnum.HPV) &&
            resendAnalysisRequest(
              screeningRequest.cancerScreening.id,
              user.ssn,
              user.name,
              screeningRequest.cancerScreening.requestorSsn ?? user.ssn,
              analysisType
            )
          }
          confirmText={translations.resend[lang]}
          text={translations.analysisType[lang]}
          disableConfirm={
            analysisType === null ||
            analysisType === AnalysisTypeEnum.Both ||
            (analysisType === AnalysisTypeEnum.HPV &&
              (screeningRequest.hpvTestResult?.createdDate == null || // Disabled if test request has never been sent
                (screeningRequest.hpvTestResult?.createdDate != null &&
                  screeningRequest.hpvTestResult?.conclusionDate != null))) || // Disabled if test request sent and result received
            (analysisType === AnalysisTypeEnum.Pathology &&
              (screeningRequest.pathologyTestResult?.createdDate == null || // Disabled if test request has never been sent
                (screeningRequest.pathologyTestResult?.createdDate != null &&
                  screeningRequest.pathologyTestResult?.conclusionDate !=
                    null))) // Disabled if test request sent and result received
          }
          loading={infoLoading}
        >
          {getCreateTestRequestDialogContent()}
        </ConfirmDialog>

        <ConfirmDialog
          open={openCompleteRequestDialog}
          title={translations.completeResults[lang]}
          onCancel={() => clearDialogs()}
          onConfirm={() =>
            screeningRequest.cancerScreening.id &&
            user.ssn &&
            completeCancerScreening(
              screeningRequest.cancerScreening.id,
              user.ssn
            )
          }
          confirmText={translations.confirm[lang]}
          text={translations.completeScreeningResultsText[lang]}
          disableConfirm={
            screeningRequest.cancerScreening.screeningStatus !==
            screeningStatuses.ALL_RESULTS_ARRIVED
          }
        ></ConfirmDialog>
        <ConfirmDialog
          open={openPrintDialog}
          title={translations.reprintScreeningNumber[lang]}
          onCancel={() => clearDialogs()}
          onConfirm={() => {
            shellHelper.printTemplateLabel(
              screeningRequest.cancerScreening,
              screeningRequest.cancerScreening?.specimenId || ""
            );
            clearDialogs();
          }}
          confirmText={translations.print[lang]}
          text={translations.reprintScreeningNumberTitle[lang]}
        ></ConfirmDialog>
        <ConfirmDialog
          open={openRemoveTestsDialog}
          title={translations.removeScreeningTests[lang]}
          onCancel={() => clearDialogs()}
          onConfirm={() => {
            if (screeningRequest.cancerScreening.id && user.ssn) {
              removeScreeningTestsAndNotifyInParent(
                screeningRequest.cancerScreening.id,
                user.ssn
              );
              clearDialogs();
            }
          }}
          confirmText={translations.removeScreeningTests[lang]}
          text={translations.removeScreeningTests[lang]}
        ></ConfirmDialog>
      </>
    );
  }

  function getCreateTestRequestDialogContent() {
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        style={{ marginBottom: theme.spacing(0.5) }}
      >
        <Grid item xs={12}>
          <FormControlLabel
            label={translations.hpvResearch[lang]}
            onClick={() => setAnalysisType(AnalysisTypeEnum.HPV)}
            control={
              <Radio
                color="primary"
                checked={AnalysisTypeEnum.HPV === analysisType}
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label={translations.cellularResearch[lang]}
            onClick={() => setAnalysisType(AnalysisTypeEnum.Pathology)}
            control={
              <Radio
                color="primary"
                checked={AnalysisTypeEnum.Pathology === analysisType}
              />
            }
          />
        </Grid>
      </Grid>
    );
  }

  async function completeCancerScreening(id: number, userSsn: string) {
    await completeCancerScreeningInParent(id, userSsn);

    clearDialogs();
  }

  async function createAnalysisRequest(
    id: number,
    userSsn: string,
    userName: string,
    originalUserSsn: string,
    analysisType: number
  ) {
    setInfoLoading(true);
    await createAnalysisRequestInParent(
      id,
      userSsn,
      userName,
      originalUserSsn,
      analysisType
    );
    setInfoLoading(false);
    clearDialogs();
  }

  function resendTestRequestDisabled(
    screeningRequest: GetCancerScreeningResultWithQueryModel
  ): boolean {
    if (
      (screeningRequest.hpvTestResult?.createdDate != null &&
        screeningRequest.hpvTestResult?.conclusionDate == null) ||
      (screeningRequest.pathologyTestResult?.createdDate != null &&
        screeningRequest.pathologyTestResult?.conclusionDate == null)
    ) {
      // Not disabled if a test request has been sent and no result has been received
      return false;
    }
    return true;
  }

  async function resendAnalysisRequest(
    id: number,
    userSsn: string,
    userName: string,
    originalUserSsn: string,
    analysisType: number
  ) {
    setInfoLoading(true);
    await resendAnalysisRequestInParent(
      id,
      userSsn,
      userName,
      originalUserSsn,
      analysisType
    );
    setInfoLoading(false);
    clearDialogs();
  }

  function clearDialogs() {
    setOpenCreateTestRequestDialog(false);
    setOpenResendTestRequestDialog(false);
    setOpenCompleteRequestDialog(false);
    setOpenPrintDialog(false);
    setOpenRemoveDialog(false);
    setAnalysisType(null);
  }
};

export default SentScreeningRequestDetails;
